/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-console */
import { useTranslate } from 'hooks/useTranslate';
import {
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { Modal } from 'react-bootstrap';
import { useMixpanel } from 'react-mixpanel-browser';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getExcelPartnersWithHeaders } from 'utils/getExcelData';
import { read } from 'xlsx';

import { store } from 'redux/store';
import { setModal } from 'modules/_shared/redux/modalActions';
import { addAlert } from 'modules/_shared/redux/alertActions';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import { createPartnerCategory } from 'modules/partners/redux/partnerCategoryActions';
import {
  addPartnersBulk,
  getPartners,
} from 'modules/partners/redux/partnerActions';

import { getActualSociety } from 'utils/filters';
import trackEvent from 'utils/trackEvent';
import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import eventTypes from 'constants/eventTypes';
import sizes from 'constants/sizes';
import {
  getEmailColumn,
  getInvestorTypeColumn,
  getNameColumn,
  getNroIdColumn,
} from 'constants/getExcelColumns';

import UploadDocument from 'modules/documents/components/UploadDocument';
import { hasPartnersDecoupled } from 'utils/showDetails';
import PartnersUploadReview from './components/PartnersUploadReview';
import PartnersUploadProgress from './components/PartnersUploadProgress';

import errors from './constants/errors';
import getInvalidEmails from './utils/getInvalidEmails';
import { mapExcelRowsToObjects } from './utils/mapExcelRowsToObjects';
import { parsePartnerExcelDynamicColumns } from './utils/parsePartnerExcelDynamicColumns';
import {
  formatPartnersForBulkUpload,
  getExistingCifsFromPartners,
} from './utils/formatPartnerForBulkUpload';
import { chunkArray, PARTNERS_BULK_BATCH_SIZE } from './utils/chunkArray';

import '../Modals.scss';

const templateURL = process.env.REACT_APP_TEMPLATE_PARTNERS_URL;
const TYPE_FILES_ALLOWED = '.csv,.xls,.xlsx';

const UPLOAD_STEPS = {
  UPLOAD: 'upload',
  REVIEW: 'review',
  PROGRESS: 'progress',
};

const INITIAL_UPLOAD_PROGRESS = {
  total: 0,
  processed: 0,
  created: 0,
  skipped: 0,
  failed: 0,
};

const UploadPartnerModal = () => {
  const { t } = useTranslate();
  const { societyId } = useParams();
  const dispatch = useDispatch();
  const mixpanel = useMixpanel();

  const user = useSelector((state) => state.user);
  const actualSociety = useSelector((state) => state.society?.actualSociety);
  const partnerCategories = useSelector((state) => state.partnerCategories);
  const partners = useSelector((state) => state.partners);
  const currentModal = useSelector((state) => state.modal);

  const [step, setStep] = useState(UPLOAD_STEPS.UPLOAD);
  const [file, setFile] = useState();
  const [isValidFile, setIsValidFile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');

  const [selectedPartners, setSelectedPartners] = useState([]);
  const [newCategories, setNewCategories] = useState([]);
  const [previewPartners, setPreviewPartners] = useState([]);
  const [dynamicExcelColumns, setDynamicExcelColumns] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(INITIAL_UPLOAD_PROGRESS);

  const currentSocietyPartners = useMemo(() => {
    if (hasPartnersDecoupled()) {
      return partners;
    }
    return actualSociety?.partners;
  }, [partners, actualSociety?.partners]);

  const existingCifs = useMemo(
    () => getExistingCifsFromPartners(currentSocietyPartners),
    [currentSocietyPartners]
  );

  const uploadNewCategories = async (categories) => {
    const categoriesToCreate = categories.map((category) => ({
      name: category,
      description: '',
      society: actualSociety['_id'],
    }));

    const uploadPromises = categoriesToCreate.map((category) =>
      store.dispatch(createPartnerCategory(category, false))
    );

    const uploadedCategories = await Promise.all(uploadPromises);

    return uploadedCategories.reduce((acc, curr) => {
      if (curr && curr.name && curr['_id']) {
        acc[curr.name] = curr['_id'];
      }
      return acc;
    }, {});
  };

  const findDuplicateCifInTemplate = (partnerList) => {
    const result = [];
    for (let i = 0; i < partnerList.length - 1; i += 1) {
      for (let j = i + 1; j < partnerList.length; j += 1) {
        if (getNroIdColumn(partnerList[i]) === getNroIdColumn(partnerList[j])) {
          result.push(getNroIdColumn(partnerList[i]));
        }
      }
    }
    return result;
  };

  const findNewPartnerCategoriesInTemplate = (partnerList) => {
    const existingCategoriesInSociety = partnerCategories?.map((category) =>
      category?.name?.toUpperCase()
    );
    const existingCategoriesInExcel = partnerList
      .map((partner) => getInvestorTypeColumn(partner))
      .filter((category) => category);

    const uniqueCategoriesInExcel = [...new Set(existingCategoriesInExcel)];

    return uniqueCategoriesInExcel.filter(
      (category) =>
        !existingCategoriesInSociety.includes(category?.toUpperCase())
    );
  };

  const hasDuplicatedFields = (partnerList) => {
    for (let i = 0; i < partnerList.length - 1; i += 1) {
      for (let j = i + 1; j < partnerList.length; j += 1) {
        if (
          getNroIdColumn(partnerList[i]) === getNroIdColumn(partnerList[j]) &&
          (getNameColumn(partnerList[i])?.toUpperCase() !==
            getNameColumn(partnerList[j])?.toUpperCase() ||
            getEmailColumn(partnerList[i]) !== getEmailColumn(partnerList[j]) ||
            (getEmailColumn(partnerList[i]) === '' &&
              getEmailColumn(partnerList[j]) === ''))
        ) {
          return true;
        }
      }
    }
    return false;
  };

  const tagExistingPartners = (partnersList) => {
    if (!existingCifs?.length) return partnersList;

    return partnersList.map((partner) => {
      if (existingCifs.includes(getNroIdColumn(partner))) {
        return { ...partner, isExisting: true };
      }
      return partner;
    });
  };

  const updateDuplicatedCifWarning = (partnersList) => {
    const duplicatedInSociety = [
      ...new Set(
        partnersList
          .filter((partner) => partner.isExisting)
          .map((partner) => getNroIdColumn(partner))
      ),
    ];

    if (duplicatedInSociety.length > 0) {
      setWarningMessage(
        `${errors.SOCIETY_DUPLICATED_PARTNERS} (${duplicatedInSociety.join(
          ' | '
        )}). ${t('TheseShareholdersWontBeUploaded')}`
      );
    } else {
      setWarningMessage('');
    }
  };

  const handleSelectFile = useCallback(
    async (currentFile) => {
      if (!currentFile) return null;

      setFile(currentFile);
      setErrorMessage('');
      setWarningMessage('');
      setPreviewPartners([]);
      setDynamicExcelColumns(null);

      try {
        const data = await currentFile.arrayBuffer();
        const workbook = read(data);
        const excelData = getExcelPartnersWithHeaders(workbook);

        if (excelData?.error) {
          setIsValidFile(false);
          setErrorMessage(errors.DATA_WRONG_MODEL);
          setSelectedPartners([]);
          return null;
        }

        const { headers, rows } = excelData;
        const parsedDynamicColumns = parsePartnerExcelDynamicColumns(
          headers,
          partnerCategories
        );

        if (parsedDynamicColumns.invalidCategoryIds.length > 0) {
          setIsValidFile(false);
          setErrorMessage(
            `${
              errors.INVALID_PARTNER_CATEGORY_COLUMNS
            } (${parsedDynamicColumns.invalidCategoryIds.join(' | ')}). ${t(
              'UpdateInfoAndTryAgain'
            )}`
          );
          setSelectedPartners([]);
          return null;
        }

        const newPartners = mapExcelRowsToObjects(headers, rows);
        const invalidEmails = getInvalidEmails(newPartners);
        const duplicatedInTemplate = [
          ...new Set(findDuplicateCifInTemplate(newPartners)),
        ];

        if (newPartners.length === 0) {
          setIsValidFile(false);
          setErrorMessage(errors.DATA_NOT_FOUND);
          setSelectedPartners([]);
        } else if (invalidEmails?.length > 0) {
          setIsValidFile(false);
          setErrorMessage(
            `${errors.INVALID_EMAILS} (${invalidEmails.join(' | ')}). ${t(
              'UpdateInfoAndTryAgain'
            )}`
          );
          setSelectedPartners([]);
        } else if (duplicatedInTemplate.length > 0) {
          setIsValidFile(false);
          setErrorMessage(
            `${
              errors.TEMPLATE_DUPLICATED_PARTNERS
            } (${duplicatedInTemplate.join(' | ')}). ${t(
              'UpdateInfoAndTryAgain'
            )}`
          );
          setSelectedPartners([]);
        } else if (hasDuplicatedFields(newPartners)) {
          setIsValidFile(false);
          setErrorMessage(errors.DUPLICATED_FIELDS);
          setSelectedPartners([]);
        } else {
          const taggedPartners = tagExistingPartners(newPartners);
          setIsValidFile(true);
          setSelectedPartners(taggedPartners);
          setDynamicExcelColumns(parsedDynamicColumns);
          setNewCategories(findNewPartnerCategoriesInTemplate(newPartners));
          updateDuplicatedCifWarning(taggedPartners);
        }
      } catch (error) {
        setIsValidFile(false);
        setErrorMessage(errors.DATA_WRONG_MODEL);
        setSelectedPartners([]);
      }

      return null;
      // eslint-disable-next-line react-hooks/exhaustive-deps -- helpers close over existingCifs, partnerCategories and t
    },
    [existingCifs, partnerCategories, t]
  );

  const buildPreviewPartners = async () => {
    let categoriesMap = {};

    if (newCategories.length > 0) {
      categoriesMap = await uploadNewCategories(newCategories);
    }

    return formatPartnersForBulkUpload({
      selectedPartners,
      existingCifs,
      actualSociety,
      partnerCategories,
      uploadedCategoriesMap: categoriesMap,
      dynamicExcelColumns,
      t,
    });
  };

  const handleContinueToReview = async () => {
    try {
      setIsLoading(true);
      const formatted = await buildPreviewPartners();

      if (!formatted.length) {
        errorSwal(t('TheseShareholdersWontBeUploaded'));
        return;
      }

      setPreviewPartners(formatted);
      setStep(UPLOAD_STEPS.REVIEW);
    } catch (error) {
      errorSwal(t('ErrorUploadingPartners'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmUpload = async () => {
    const total = previewPartners.length;
    const accumulated = {
      created: [],
      skipped: [],
      failed: [],
    };

    const basePayload = {
      society: actualSociety['_id'],
      user: user['_id'],
      userEmail: user.email,
      societyName: actualSociety?.name,
    };

    const chunks = chunkArray(previewPartners, PARTNERS_BULK_BATCH_SIZE);

    try {
      setIsLoading(true);
      setStep(UPLOAD_STEPS.PROGRESS);
      setUploadProgress({
        ...INITIAL_UPLOAD_PROGRESS,
        total,
      });

      let processed = 0;

      for (const chunk of chunks) {
        try {
          const result = await store.dispatch(
            addPartnersBulk(
              {
                ...basePayload,
                partners: chunk,
              },
              { showAlert: false }
            )
          );

          if (result) {
            accumulated.created.push(...(result.created || []));
            accumulated.skipped.push(...(result.skipped || []));
            accumulated.failed.push(...(result.failed || []));
          } else {
            chunk.forEach((partner) => {
              accumulated.failed.push({
                cif: partner.cif,
                error: t('ErrorUploadingPartners'),
              });
            });
          }
        } catch (batchError) {
          chunk.forEach((partner) => {
            accumulated.failed.push({
              cif: partner.cif,
              error: t('ErrorUploadingPartners'),
            });
          });
        }

        processed += chunk.length;
        setUploadProgress({
          total,
          processed,
          created: accumulated.created.length,
          skipped: accumulated.skipped.length,
          failed: accumulated.failed.length,
        });
      }

      if (accumulated.created.length === 0 && accumulated.failed.length > 0) {
        errorSwal(t('ErrorUploadingPartners'));
        setStep(UPLOAD_STEPS.REVIEW);
        return;
      }

      if (accumulated.created.length > 0) {
        dispatch(addAlert(alertBodyTypes.SOCIETY_UPDATED));
      }

      await store.dispatch(getPartners(actualSociety['_id']));

      trackEvent(mixpanel, eventTypes.ADD_PARTNERS, {
        userId: user?._id,
        userName: user?.name,
        userEmail: user?.email,
        societyId: actualSociety?._id,
        societyName: actualSociety?.name,
        operation: eventTypes.ADD_PARTNERS,
        totalPartners: accumulated.created.length,
        skippedPartners: accumulated.skipped.length,
        failedPartners: accumulated.failed.length,
      });

      if (accumulated.failed.length > 0 || accumulated.skipped.length > 0) {
        successSwal(t('PartnersBulkUploadPartialSuccess'));
      } else {
        successSwal(t('PartnersBulkUploadSuccess'));
      }

      dispatch(setModal(null));
    } catch (error) {
      errorSwal(t('ErrorUploadingPartners'));
      setStep(UPLOAD_STEPS.REVIEW);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToUpload = () => {
    setStep(UPLOAD_STEPS.UPLOAD);
  };

  const isContinueDisabled = () =>
    !file || !isValidFile || !selectedPartners?.length;

  useEffect(() => {
    if (actualSociety?.['_id'] !== societyId) {
      getActualSociety(user, societyId);
    }
  }, [actualSociety, user, societyId]);

  useEffect(() => {
    if (!isValidElement(currentModal)) return;

    const isWideStep =
      step === UPLOAD_STEPS.REVIEW || step === UPLOAD_STEPS.PROGRESS;
    const nextSize = isWideStep ? sizes.XXL : sizes.MD;

    if (currentModal.props?.size !== nextSize) {
      dispatch(setModal(cloneElement(currentModal, { size: nextSize })));
    }
  }, [step, currentModal, dispatch]);

  if (isLoading && step === UPLOAD_STEPS.PROGRESS) {
    return <PartnersUploadProgress {...uploadProgress} />;
  }

  if (step === UPLOAD_STEPS.REVIEW) {
    const hasValidationErrors = previewPartners.some(
      (partner) => partner.hasValidationErrors
    );

    return (
      <PartnersUploadReview
        partners={previewPartners}
        totalPartnersInFile={selectedPartners.length}
        dynamicExcelColumns={dynamicExcelColumns}
        partnerCategories={partnerCategories}
        onBack={handleBackToUpload}
        onConfirm={handleConfirmUpload}
        isConfirmDisabled={isLoading || hasValidationErrors}
      />
    );
  }

  return (
    <>
      <Modal.Header>
        <h5 className="modal-title">{t('List')}</h5>
        <a
          className="close cursor-pointer"
          onClick={() => dispatch(setModal(null))}
        >
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col mb-4">
            <a
              href={templateURL}
              target="_blank"
              className="btn btn-block btn-white btn-outline-light"
              rel="noopener noreferrer"
            >
              <em className="icon ni ni-file-download" />
              <span className="fs-16px"> {t('DownloadExampleTemplateTo')}</span>
            </a>
          </div>
        </div>

        <form className="form-validate is-alter">
          <div className="row mb-4">
            <div className="col">
              <UploadDocument
                label="ListOfPartners"
                inputLabel={file?.name || t('DragFileToUpload')}
                file={file}
                setFile={handleSelectFile}
                accept={TYPE_FILES_ALLOWED}
              />

              {file && isValidFile && !warningMessage && (
                <div className="alert alert-icon alert-success" role="alert">
                  <em className="icon ni ni-check-circle" />
                  <strong>{t('ValidatedFile')}</strong>.&nbsp;
                  {selectedPartners?.length > 0 &&
                    newCategories.length === 0 &&
                    t('FoundPartnersUpload', {
                      partners: selectedPartners.length,
                    })}
                  {selectedPartners?.length > 0 &&
                    newCategories.length > 0 &&
                    t('FoundPartnersAndCategoriesUpload', {
                      partners: selectedPartners.length,
                      categories: newCategories.length,
                    })}
                </div>
              )}

              {file && isValidFile && warningMessage && (
                <div className="alert alert-icon alert-warning" role="alert">
                  <em className="icon ni ni-alert-circle" />
                  <strong>{t('Warning')}</strong>. {warningMessage}
                </div>
              )}

              {file && !isValidFile && errorMessage && (
                <div className="alert alert-icon alert-danger" role="alert">
                  <em className="icon ni ni-alert-circle" />
                  <strong>{t('Non-ValidFile')}</strong>. {errorMessage}
                </div>
              )}
            </div>
          </div>

          <button
            type="button"
            className="btn btn-lg btn-primary"
            onClick={handleContinueToReview}
            disabled={isContinueDisabled() || isLoading}
            data-testid="continue-to-review-button"
          >
            {isLoading ? t('Loading') : t('ContinueToReview')}
          </button>
        </form>
      </Modal.Body>

      {actualSociety?.sendNotifications && (
        <Modal.Footer className="bg-light">
          <span className="sub-text">
            {t('ThePartnersWontReceiveNotification')}
          </span>
        </Modal.Footer>
      )}
    </>
  );
};

export default UploadPartnerModal;
